/**
 * Safe wrapper around localStorage and sessionStorage that guards against
 * QuotaExceededError (DOMException 22/1014), private-browsing restrictions,
 * and disabled storage environments.
 */

const EXPENDABLE_CACHE_KEYS = [
  'testara_testRuns',
  'testara_testCases',
  'testara_testPlans',
  'testara_projects',
  'testara_users',
  'testara_environments',
  'testara_labels',
  'testara_github_config',
  'testara_settings_active_tab',
  'testara_settings_integrations_panel',
];

function isQuotaExceededError(err) {
  if (!err) return false;
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 ||
    err.code === 1014 ||
    err.number === -2147024882 ||
    (typeof err.message === 'string' && err.message.toLowerCase().includes('quota'))
  );
}

function evictExpendableCaches() {
  if (typeof window === 'undefined' || !window.localStorage) return 0;
  let evicted = 0;
  try {
    for (const key of EXPENDABLE_CACHE_KEYS) {
      if (window.localStorage.getItem(key) !== null) {
        window.localStorage.removeItem(key);
        evicted++;
      }
    }
    // Also remove any tasks cache or draft run keys
    const allKeys = Object.keys(window.localStorage);
    for (const k of allKeys) {
      if (k.startsWith('testara_tasks_') || k.startsWith('testara_draft_run_')) {
        window.localStorage.removeItem(k);
        evicted++;
      }
    }
  } catch {
    // Ignore errors during eviction
  }
  return evicted;
}

export const safeLocalStorage = {
  getItem(key, fallback = null) {
    if (typeof window === 'undefined' || !window.localStorage) return fallback;
    try {
      const val = window.localStorage.getItem(key);
      return val !== null ? val : fallback;
    } catch {
      return fallback;
    }
  },

  getItemJSON(key, fallback = null) {
    const raw = this.getItem(key, null);
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  },

  setItem(key, value) {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const strVal = typeof value === 'string' ? value : JSON.stringify(value);
    try {
      window.localStorage.setItem(key, strVal);
      return true;
    } catch (err) {
      if (isQuotaExceededError(err)) {
        console.warn(`[safeStorage] Quota exceeded on key "${key}". Evicting expendable caches...`);
        const count = evictExpendableCaches();
        if (count > 0) {
          try {
            window.localStorage.setItem(key, strVal);
            return true;
          } catch (retryErr) {
            console.warn(`[safeStorage] Failed to set "${key}" even after cache eviction:`, retryErr);
          }
        }
      } else {
        console.warn(`[safeStorage] Failed to set "${key}":`, err);
      }
      return false;
    }
  },

  removeItem(key) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },

  clear() {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.clear();
    } catch {
      // ignore
    }
  },

  evictCaches: evictExpendableCaches,
};

export const safeSessionStorage = {
  getItem(key, fallback = null) {
    if (typeof window === 'undefined' || !window.sessionStorage) return fallback;
    try {
      const val = window.sessionStorage.getItem(key);
      return val !== null ? val : fallback;
    } catch {
      return fallback;
    }
  },

  getItemJSON(key, fallback = null) {
    const raw = this.getItem(key, null);
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  },

  setItem(key, value) {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    const strVal = typeof value === 'string' ? value : JSON.stringify(value);
    try {
      window.sessionStorage.setItem(key, strVal);
      return true;
    } catch (err) {
      console.warn(`[safeStorage] Failed to set sessionStorage "${key}":`, err);
      return false;
    }
  },

  removeItem(key) {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },

  clear() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.clear();
    } catch {
      // ignore
    }
  },
};

export default safeLocalStorage;
